import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { store } from './store/store';
import { restoreAuth } from './store/slices/authSlice';
import AdminLayout from './components/templates/AdminLayout';
import LoginPage from './pages/Auth/LoginPage';
import DashboardPage from './pages/Dashboard/DashboardPage';
import UsersPage from './pages/Users/UsersPage';
import ProductsPage from './pages/Products/ProductsPage';
import ChatsPage from './pages/Chats/ChatsPage';
import BankTransfersPage from './pages/BankTransfers/BankTransfersPage';
import ReportsPage from './pages/Reports/ReportsPage';
import SettingsPage from './pages/Settings/SettingsPage';
import ProfilePage from './pages/Profile/ProfilePage';
import AppRedirectPage from './pages/AppRedirect/AppRedirectPage';
import NotificationsPage from './pages/Notifications/NotificationsPage';
import AnnouncementsPage from './pages/Announcements/AnnouncementsPage';
import PayoutRequestsPage from './pages/PayoutRequests/PayoutRequestsPage';
import ProductCommentsPage from './pages/ProductComments/ProductCommentsPage';
import FeaturedProductsPage from './pages/FeaturedProducts/FeaturedProductsPage';
import HomeSettingsPage from './pages/HomeSettings/HomeSettingsPage';
import CouponsPage from './pages/Coupons/CouponsPage';
import TutorialStatsPage from './pages/TutorialStats/TutorialStatsPage';
import ProtectedRoute from './components/ProtectedRoute';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

// 認証状態初期化コンポーネント
const AuthInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    // アプリ起動時に認証状態を復元
    dispatch(restoreAuth());
  }, [dispatch]);

  return <>{children}</>;
};

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthInitializer>
          <Router>
            <Routes>
              {/* ログイン画面 */}
              <Route path="/login" element={<LoginPage />} />
              
              {/* アプリダウンロードリダイレクトページ */}
              <Route path="/app" element={<AppRedirectPage />} />
              <Route path="/app/product/:id" element={<AppRedirectPage />} />
              <Route path="/app/profile/:id" element={<AppRedirectPage />} />
              
              {/* 保護されたルート */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <AdminLayout>
                      <Routes>
                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                        <Route path="/dashboard" element={<DashboardPage />} />
                        <Route path="/users" element={<UsersPage />} />
                        <Route path="/products" element={<ProductsPage />} />
                        <Route path="/product-comments" element={<ProductCommentsPage />} />
                        <Route path="/featured-products" element={<FeaturedProductsPage />} />
                        <Route path="/home-settings" element={<HomeSettingsPage />} />
                        <Route path="/coupons" element={<CouponsPage />} />
                        <Route path="/tutorial-stats" element={<TutorialStatsPage />} />
                        <Route path="/chats" element={<ChatsPage />} />
                        <Route path="/bank-transfers" element={<BankTransfersPage />} />
                        <Route path="/payout-requests" element={<PayoutRequestsPage />} />
                        <Route path="/reports" element={<ReportsPage />} />
                        <Route path="/notifications" element={<NotificationsPage />} />
                        <Route path="/announcements" element={<AnnouncementsPage />} />
                        <Route path="/settings" element={<SettingsPage />} />
                        <Route path="/profile" element={<ProfilePage />} />
                      </Routes>
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Router>
        </AuthInitializer>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
